// Reusable SSE client for the dashboard conversation lane
// (POST /api/eric/dispatch/stream). Mirrors the wire vocabulary the worker
// emits (worker/routes/eric-dispatch.ts):
//   { t:'tool',  name }              — a tool started ("Looking up…")
//   { t:'text',  delta }             — a chunk of the reply
//   { t:'thread', thread_id, … }     — which thread this turn filed into (Home v2)
//   { t:'done',  envelope }          — final DispatchResponse (result card + undo)
//   { t:'error', message }           — transport fault (a `done` still follows)
//
// Extracted so the new Home v2 thread composer and (later) TaskEricInput share
// ONE stream implementation instead of duplicating the parse loop.

import type { DispatchResponse } from '@/components/dashboard/agentic/types'

export type ThreadFrame = {
  thread_id: string
  customer_id: string | null
  is_unfiled: boolean
  title: string | null
}

// The staged photo's classify-lane extraction, shipped to the worker INSTEAD of
// image pixels (locked 2026-06-10 — no pixel fallback: MB-scale base64 bodies
// died in the brain's 4.5MB body limit). The worker validates it
// (worker/lib/eric-photo-analysis.ts parsePhotoContext — drop-don't-400) and
// renders the `<photo_analysis>` requestContext block the brain acts on. Field
// names mirror the classify response's snake_case wire shape.
export type EricPhotoContext = {
  r2_key: string
  kind: 'vin_sticker' | 'odometer' | 'receipt' | 'other'
  confidence: 'high' | 'medium' | 'low'
  summary: string
  extracted: {
    vin: string | null
    mileage_km: number | null
    total_cents: number | null
    currency: string | null
  }
  // NHTSA decode of extracted.vin (src/lib/vin.ts decodeVin) — null when the
  // VIN didn't validate/decode; the brain then confirms before any write.
  decoded: { year: number | null; make: string | null; model: string | null } | null
}

export type EricStreamHandlers = {
  onTool?: (name: string) => void
  onText?: (delta: string) => void
  onThread?: (info: ThreadFrame) => void
  onDone?: (envelope: DispatchResponse) => void
  onError?: (message: string) => void
}

/**
 * Open and consume the dispatch stream. Resolves with the final envelope (or
 * null if the stream closed without a `done`). Throws on a pre-stream failure
 * (rate_limit / duplicate / 5xx come back as JSON, not SSE) or a missing token;
 * callers treat an AbortError (unmount / superseded) as a silent no-op.
 */
export async function dispatchEricStream(opts: {
  prompt: string
  threadId?: string | null
  photo?: EricPhotoContext
  // A clip already transcribed by POST /api/eric/voice/transcribe. The worker
  // re-reads the transcript from the voice_messages row server-side (`prompt`
  // here is display-only for a voice turn), marks the audit row
  // input_modality='voice', and persists the replayable bubble on the thread.
  voiceMessageId?: string | null
  getToken: () => Promise<string | null>
  signal?: AbortSignal
  handlers: EricStreamHandlers
}): Promise<DispatchResponse | null> {
  const { prompt, threadId, photo, voiceMessageId, getToken, signal, handlers } = opts

  const token = await getToken()
  if (!token) throw new Error('missing_token')

  const res = await fetch('/api/eric/dispatch/stream', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      prompt,
      ...(threadId ? { thread_id: threadId } : {}),
      ...(photo ? { photo } : {}),
      ...(voiceMessageId ? { voice_message_id: voiceMessageId } : {}),
    }),
    signal,
  })
  if (!res.ok || !res.body) throw new Error(`dispatch_stream_${res.status}`)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let final: DispatchResponse | null = null

  readLoop: for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    const frames = buf.split('\n\n')
    buf = frames.pop() ?? ''
    for (const frame of frames) {
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
      if (!dataLine) continue
      const payload = dataLine.slice(5).trim()
      if (!payload) continue
      let evt: {
        t?: string
        name?: string
        delta?: string
        envelope?: DispatchResponse
        message?: string
        thread_id?: string
        customer_id?: string | null
        is_unfiled?: boolean
        title?: string | null
      }
      try {
        evt = JSON.parse(payload)
      } catch {
        continue
      }
      if (evt.t === 'tool' && evt.name) {
        handlers.onTool?.(evt.name)
      } else if (evt.t === 'text' && evt.delta) {
        handlers.onText?.(evt.delta)
      } else if (evt.t === 'thread' && evt.thread_id) {
        handlers.onThread?.({
          thread_id: evt.thread_id,
          customer_id: evt.customer_id ?? null,
          is_unfiled: !!evt.is_unfiled,
          title: evt.title ?? null,
        })
      } else if (evt.t === 'done' && evt.envelope) {
        final = evt.envelope
        handlers.onDone?.(evt.envelope)
        break readLoop
      } else if (evt.t === 'error') {
        handlers.onError?.(evt.message ?? 'stream_error')
      }
    }
  }

  return final
}
